/*
 * Jira Ticket:
 * Created Date: Thu, 26th Mar 2020, 15:20:44 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React from 'react';
import {
  ArrayInput,
  Edit,
  ImageInput,
  required,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  useNotify,
  useTranslate,
  usePermissions,
  Title
} from 'react-admin';
import { AccessDenied } from 'Layout';
import { isSuperAdmin } from 'utils/roles';
import { PreviewImageField } from 'Components/Fields';
import { activeInActiveOptions } from 'utils/choices';
import { validateQualificationInput } from 'utils/validation';
import { columnStyles } from 'styles';
import { onDropRejected } from 'utils';

const maxFileSize = 2097152; // 2MB in bytes
const allowedFileTypes = 'image/jpg, image/jpeg, image/png';

const SanitizedForm = ({ classes, translate }) => {
  return (
    <div className={classes.root}>
      <div className={classes.column}>
        <TextInput
          source="name"
          label={translate('resources.TowerType.name', { smart_count: 1 })}
          validate={required()}
        />
      </div>
      <div className={classes.column}>
        <SelectInput
          source="status"
          choices={activeInActiveOptions}
          label={'resources.TowerType.fields.status'}
          validate={required()}
        />
      </div>
    </div>
  );
};

const IndexedTextInput = props => {
  const { label, record, translate, ...rest } = props;
  const question = translate('resources.TowerType.fields.question');
  const index = props.source.split('[')[1].split(']')[0];
  return (
    <TextInput
      label={`${question} ${Number(index) + 1}`}
      validate={required()}
      multiline={true}
      fullWidth
      {...rest}
    />
  );
};

const TowerTypeEdit = props => {
  const { permissions } = usePermissions();
  const classes = columnStyles();
  const translate = useTranslate();
  const notify = useNotify();
  const rest = { classes, translate, ...props };

  return isSuperAdmin(permissions) ? (
    <>
     <Title title={`resources.General.cmsTitle`} />
    <Edit
      title={'resources.TowerType.fields.towerDetail'}
      undoable={false}
      {...props}
    >
      <SimpleForm redirect={false}>
        <SanitizedForm {...rest} />
        <TextInput
          source="qualificationIds"
          fullWidth={true}
          label={'resources.TowerType.fields.qualifications'}
          helperText="resources.TowerType.fields.qualificationHelperText"
          validate={validateQualificationInput}
        />
        <ArrayInput source="questions" validate={required()}>
          <SimpleFormIterator>
            <IndexedTextInput
              source="question"
              validate={required()}
              multiline={true}
              translate={translate}
              fullWidth
            />
            <ImageInput
              source="image"
              label={'resources.TowerType.fields.media'}
              maxSize={maxFileSize}
              accept={allowedFileTypes}
              options={{
                onDropRejected: files =>
                  onDropRejected({
                    files,
                    translate,
                    notify,
                    maxFileSize,
                    allowedFileTypes,
                  }),
              }}
              validate={required()}
            >
              <PreviewImageField />
            </ImageInput>
          </SimpleFormIterator>
        </ArrayInput>
      </SimpleForm>
    </Edit>
    </>
  ) : (
    <AccessDenied />
  );
};

export default TowerTypeEdit;
